import type { IAnswer, ISuitabilityTest, ISuitabilityTestVersion } from "@/services/SuitabilityTestsService";
import
{
    getLatestSignedSuitabilityTest,
    getSuitabilityTestExpiresAt,
    isSuitabilityTestInForce,
    matchSuitabilityAnswersToCurrentVersion,
} from "@/screen/ContractManagement/helpers/suitabilityTestValidity";

/** Por qué se abre el formulario: caducado por fecha, por versión nueva, o porque el usuario quiere actualizarlo. */
export type SuitabilityRenewalReason = "age" | "version" | "voluntary";

export interface SuitabilityRenewalView
{
    redirectToList: boolean;
    initialAnswers: IAnswer[];
    questionnaireChanged: boolean;
    expiresAt: Date | null;
    reason: SuitabilityRenewalReason | null;
}

const EmptyRenewalView: SuitabilityRenewalView =
{
    redirectToList: true,
    initialAnswers: [],
    questionnaireChanged: false,
    expiresAt: null,
    reason: null,
};

function resolveSuitabilityRenewalReason(
    latest: ISuitabilityTest,
    currentVersion: ISuitabilityTestVersion,
    now: Date
): SuitabilityRenewalReason
{
    const versionDiffers = currentVersion.id != null && latest.suitabilityTestVersionId !== currentVersion.id;
    if(versionDiffers)
    {
        return "version";
    }

    if(!isSuitabilityTestInForce(latest, now))
    {
        return "age";
    }

    return "voluntary";
}

/**
 * Prepara las respuestas de partida. Sin test firmado o sin cuestionario vigente se vuelve al listado.
 * Un test vigente no echa de la pantalla: es una actualización voluntaria.
 */
export function buildSuitabilityRenewalView(
    tests: ISuitabilityTest[] | null | undefined,
    currentVersion: ISuitabilityTestVersion | null | undefined,
    previousVersion: ISuitabilityTestVersion | null | undefined,
    now: Date = new Date()
): SuitabilityRenewalView
{
    const latest = getLatestSignedSuitabilityTest(tests);
    if(!latest || !currentVersion)
    {
        return EmptyRenewalView;
    }

    const versionForMatch = previousVersion ?? currentVersion;
    const match = matchSuitabilityAnswersToCurrentVersion(latest.answers, versionForMatch, currentVersion);
    const reason = resolveSuitabilityRenewalReason(latest, currentVersion, now);

    return{
        redirectToList: false,
        initialAnswers: match.answers,
        questionnaireChanged: match.questionnaireChanged,
        expiresAt: reason === "age" ? getSuitabilityTestExpiresAt(latest) : null,
        reason: reason,
    };
}

export function buildIntervenerDisplayName(firstName?: string | null, lastName?: string | null, secondLastName?: string | null): string
{
    return [firstName, lastName, secondLastName]
        .map((part) => part?.trim() ?? "")
        .filter((part) => part !== "")
        .join(" ");
}
